import _ from 'lodash';
import { PropsWithChildren, useRef, useState } from 'react';
import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { AddIcon, Button, Empty, Paragraph, Popover, Skeleton, tokens } from '@treams/treams-ui';

import CreateJobPosition from './CreateJobPosition';
import JobPosition from './JobPosition';

const Grid = styled(Flex)`
	padding: 6px 16px;
	border-radius: 5px;
	border: 1px solid transparent;
	align-items: center;
	flex-wrap: nowrap;
`;

const Border = styled(Flex)`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 60px;
	padding: 6px;
	border-radius: 5px;
	border: 1px solid ${tokens.border.subtle};
	align-items: center;
	pointer-events: none;
`;

function ReverseScroll(props: PropsWithChildren) {
	const ref = useRef<HTMLDivElement>(null);
	const contents = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (!ref.current || !contents.current) {
			return;
		}

		ref.current.scrollLeft = ref.current.scrollWidth - ref.current.clientWidth;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!ref, !!contents]);

	return (
		<div ref={ref} style={{ overflowX: 'auto' }}>
			<div ref={contents}>{props.children}</div>
		</div>
	);
}

export default function JobPositions(props: { id: string }) {
	const { t } = useTranslation();
	const multiRoleConversion = useFeature(graphql.FeatureName.MultiRoleConversion);

	const query = graphql.useJobPositionsQuery({
		variables: { id: props.id },
		fetchPolicy: 'network-only',
	});

	const [adding, setAdding] = useState(false);

	const create = (
		<CreateJobPosition
			user={query.data?.user as graphql.User}
			onCancel={() => setAdding(false)}
			onCreate={async () => {
				await query.refetch();
			}}
			onDismiss={() => setAdding(false)}
		/>
	);

	const addButton = (
		<Popover.Target visible={adding} contents={create}>
			<Button
				type="secondary"
				icon={<AddIcon />}
				title={t('settings.employees.jobPositions.add')}
				onClick={() => setAdding(true)}
			/>
		</Popover.Target>
	);

	return (
		<Box mb={5}>
			{query.data ? (
				<Paragraph mb={3}>
					{t(
						multiRoleConversion
							? 'settings.employees.jobPositions.role.description'
							: 'settings.employees.jobPositions.description',
						{ name: query.data.user.firstName },
					)}
				</Paragraph>
			) : (
				<Paragraph.Skeleton lines={1} mb={3} />
			)}
			{query.data ? (
				query.data.user.jobPositions.length > 0 ? (
					<Box style={{ position: 'relative' }}>
						<ReverseScroll>
							<Grid>
								<Box flexGrow={0} flexShrink={0} width={56} />
								{_.sortBy(query.data.user.jobPositions, (position) => position.startDate)
									.filter((position) => !position.isArchived)
									.map((position, index) => (
										<JobPosition
											key={position.id}
											user={query.data?.user as graphql.User}
											position={position as graphql.JobPosition}
											index={index}
										/>
									))}
								<Box mr={2} width={32} flexGrow={0} flexShrink={0} />
								<Box flexGrow={0} flexShrink={0}>
									{addButton}
								</Box>
								<Box flexGrow={0} flexShrink={0} width={6} height={1} />
							</Grid>
							<Box height={76} mt={2} />
						</ReverseScroll>
						<Border />
					</Box>
				) : (
					<Empty.Card
						title={t('settings.employees.jobPositions.empty.title')}
						description={t('settings.employees.jobPositions.empty.description', {
							name: query.data.user.firstName,
						})}
						footer={<Box mt={2}>{addButton}</Box>}
					/>
				)
			) : (
				<Grid>
					<Skeleton.DynamicBox height={48} />
				</Grid>
			)}
		</Box>
	);
}
